import { useEffect, useMemo, useRef, type KeyboardEvent, type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import {
  DEFAULT_CONFIG,
  calculateAllocation,
  calibrate,
  decide,
} from '@whaletail/signal-engine';
import { useBtc15Predictor } from '../hooks/useBtc15Predictor';
import { useBtc15FittedModels } from '../hooks/useBtc15FittedModels';
import { useBtc15History } from '../hooks/useBtc15History';
import { useBtc15ModelHistory } from '../hooks/useBtc15ModelHistory';
import { useBtc15TakeProfitHistory } from '../hooks/useBtc15TakeProfitHistory';
import { useBtc15ShadowExitPolicyReport } from '../hooks/useBtc15ShadowExitPolicyReport';
import { useCountdown } from '../hooks/useCountdown';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useBtc15Bankroll } from '../hooks/useBtc15Bankroll';
import {
  LEDGER_RULES,
  LEDGER_STRATEGIES,
  ledgerFromHistories,
  summarizeStrategy,
  type StrategySummary,
} from '../lib/bankrollLedger';
import {
  computeAccuracyWindows,
  currentPosition,
  recentFundedResults,
  toSettledRecords,
} from '../lib/btc15History';
import { emptyTakeProfitHistory } from '../lib/btc15TakeProfitHistory';
import { buildCopySummary } from '../lib/btc15Summary';
import { ApiClientError } from '../lib/apiClient';
import {
  predictCoinFlip,
  predictDice,
  predictV2,
  predictV3,
  predictV4,
} from '../lib/modelPredictions';
import { STRATEGY_NAMES, type StrategyKey } from '../lib/modelNames';
import { Btc15Decision } from '../components/btc15/Btc15Decision';
import { Btc15LiveRound } from '../components/btc15/Btc15LiveRound';
import { Btc15PriceRulesNow } from '../components/btc15/Btc15PriceRules';
import { ForwardTests } from '../components/btc15/ForwardTests';
import { useBtc15Live } from '../hooks/useBtc15Live';
import { useBtc15PriceRules } from '../hooks/useBtc15PriceRules';
import { useEthHourlyRule } from '../hooks/useEthHourlyRule';
import { useHourlyPooledRule } from '../hooks/useHourlyPooledRule';
import { useRiptideRule } from '../hooks/useRiptideRule';
import { Btc15RoundLookup } from '../components/btc15/Btc15RoundLookup';
import { Btc15SignalOptIn } from '../components/btc15/Btc15SignalOptIn';
import { Btc15ModelComparison } from '../components/btc15/Btc15ModelComparison';
import { Btc15MarketSizing } from '../components/btc15/Btc15MarketSizing';
import { Btc15Details } from '../components/btc15/Btc15Details';
import {
  Btc15TrackRecord,
  type TrackRecordRow,
  type TrackRecordSource,
} from '../components/btc15/Btc15TrackRecord';
import { Btc15ShadowExitPolicyComparison } from '../components/btc15/Btc15ShadowExitPolicyComparison';
import { Btc15ShadowSizingPolicyComparison } from '../components/btc15/Btc15ShadowSizingPolicyComparison';
import { Btc15MartingaleComparison } from '../components/btc15/Btc15MartingaleComparison';
import { Btc15Diagnostics } from '../components/btc15/Btc15Diagnostics';
import { isPredictorDataStale } from '../lib/btc15DataAge';
import { Btc15HistoryTools } from '../components/btc15/Btc15HistoryTools';
import { Disclaimer } from '../components/Disclaimer';
import { BackLink } from '../components/BackLink';
import { cameFromPaperTests } from '../lib/paperTestPages';
import { Button } from '../components/Button';
import { RefreshStatus } from '../components/RefreshStatus';
import { Skeleton } from '../components/Skeleton';
import styles from './Btc15PredictorPage.module.css';

const READ_ONLY_DISCLAIMER =
  'Read-only paper simulation. This page never places an order, holds no exchange credentials, ' +
  'and nothing shown here has been executed. Model probability, sizing, and settlement all use ' +
  'the Coinbase spot price as a proxy and are not a recommendation to buy, sell, or hold anything.';

const READ_ONLY_SUMMARY = 'Paper simulation only. No orders are placed.';

const TABS = [
  { id: 'round', label: 'This round' },
  { id: 'record', label: 'Track record' },
  { id: 'rules', label: 'Rules' },
  { id: 'research', label: 'Research' },
] as const;
type TabId = (typeof TABS)[number]['id'];
const isTabId = (v: string | null): v is TabId => TABS.some((t) => t.id === v);

/**
 * The page's four views as ARIA tabs: the live round (with what every model
 * says about it), the models' paper records, the locked forward-test rules,
 * and the retrospective what-if replays. Every panel stays mounted (only
 * hidden), so the per-round history recording that lives in this page keeps
 * running whichever tab is showing. The tab lives in `?tab=` so a view can
 * be linked to and survives a reload.
 */
function usePredictorTab(): [TabId, (tab: TabId) => void] {
  const [params, setParams] = useSearchParams();
  const { state } = useLocation();
  const raw = params.get('tab');
  const tab: TabId = isTabId(raw) ? raw : 'round';
  const setTab = (next: TabId) => {
    const nextParams = new URLSearchParams(params);
    if (next === 'round') nextParams.delete('tab');
    else nextParams.set('tab', next);
    // Keep the router state, which says whether the reader came here from
    // the Paper tests overview (and so gets a link back to it).
    setParams(nextParams, { replace: true, state });
  };
  return [tab, setTab];
}

function PredictorTabs({
  tab,
  onChange,
}: {
  tab: TabId;
  onChange: (tab: TabId) => void;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const index = TABS.findIndex((t) => t.id === tab);
    const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : null;
    const target =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? TABS.length - 1
          : step === null
            ? null
            : (index + step + TABS.length) % TABS.length;
    if (target === null) return;
    event.preventDefault();
    const next = TABS[target].id;
    onChange(next);
    refs.current[next]?.focus();
  };
  return (
    <div className={styles.tabs} role="tablist" aria-label="Predictor views">
      {TABS.map((t) => (
        <button
          key={t.id}
          ref={(el) => {
            refs.current[t.id] = el;
          }}
          type="button"
          role="tab"
          id={`btc15-tab-${t.id}`}
          aria-selected={tab === t.id}
          aria-controls={`btc15-panel-${t.id}`}
          tabIndex={tab === t.id ? 0 : -1}
          className={styles.tab}
          onClick={() => onChange(t.id)}
          onKeyDown={onKeyDown}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function TabPanel({
  id,
  active,
  children,
}: {
  id: TabId;
  active: TabId;
  children: ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      id={`btc15-panel-${id}`}
      aria-labelledby={`btc15-tab-${id}`}
      hidden={active !== id}
      className={styles.panel}
    >
      {children}
    </div>
  );
}

const RD_FILTER_NOTE =
  "A round gets no bet when its edge over the market price doesn't clear the minimum threshold.";

/** What leaves a round without a bet, per strategy; shown in its expanded row. */
const STRATEGY_NOTES: Record<StrategyKey, string> = {
  v1: 'A SKIP call places no bet.',
  'v1-tp': `Sells early once the contract's bid has gained enough over entry to clear a confidence-scaled target (3¢–15¢); otherwise rides to settlement exactly like ${STRATEGY_NAMES.v1.name}. An early sale counts as a right call.`,
  v2: RD_FILTER_NOTE,
  v3: RD_FILTER_NOTE,
  v4: RD_FILTER_NOTE,
  coinflip:
    'Always 50/50 and never SKIPs; a round gets no bet only when its edge over the market price is too small, the same gate the fitted models use.',
  dice: 'Rolled once per round; always 50/50 (4-6 calls UP, 1-3 calls DOWN) and never SKIPs; a round gets no bet only when its edge over the market price is too small, the same gate the fitted models use.',
};

export function Btc15PredictorPage() {
  const online = useOnlineStatus();
  const location = useLocation();
  const [tab, setTab] = usePredictorTab();
  // Fast price polling only while the live view is on screen.
  const live = useBtc15Live(tab === 'round');
  const query = useBtc15Predictor();
  const fittedModels = useBtc15FittedModels();
  const { history, applyResponse } = useBtc15History();
  const { history: v2Hist, applyResponse: applyV2Response } = useBtc15ModelHistory('v2');
  const { history: v3Hist, applyResponse: applyV3Response } = useBtc15ModelHistory('v3');
  const { history: v4Hist, applyResponse: applyV4Response } = useBtc15ModelHistory('v4');
  const { history: coinFlipHist, applyResponse: applyCoinFlipResponse } =
    useBtc15ModelHistory('coinflip');
  const { history: diceHist, applyResponse: applyDiceResponse } =
    useBtc15ModelHistory('dice');
  const takeProfitHistoryQuery = useBtc15TakeProfitHistory();
  const bankrollQuery = useBtc15Bankroll();
  const shadowExitPolicyReportQuery = useBtc15ShadowExitPolicyReport();
  const priceRulesQuery = useBtc15PriceRules();
  const ethHourlyRuleQuery = useEthHourlyRule();
  const hourlyPooledRuleQuery = useHourlyPooledRule();
  const riptideRuleQuery = useRiptideRule();
  const response = query.data;
  const countdownSeconds = useCountdown(response?.round.closeMs ?? null);

  const resolvedRecords = useMemo(() => toSettledRecords(history), [history]);
  const accuracy = useMemo(() => computeAccuracyWindows(history), [history]);
  const recentFunded = useMemo(() => recentFundedResults(history, 3), [history]);
  const livePosition = useMemo(
    () => (response ? currentPosition(history, response) : null),
    [history, response],
  );

  const calibration = useMemo(
    () => (response ? calibrate(response.signal, resolvedRecords) : null),
    [response, resolvedRecords],
  );
  const decision = useMemo(
    () => (response && calibration ? decide(response.signal, calibration) : null),
    [response, calibration],
  );

  // Recorded with `decision.recommendation` (the real, calibrated,
  // possibly-SKIP call), never `response.signal.action` (the model's raw,
  // never-SKIP directional score) — see recordObservation's own docstring
  // for why conflating the two silently made every round "playable".
  useEffect(() => {
    if (response && decision) applyResponse(response, decision.recommendation);
  }, [response, decision, applyResponse]);

  // The live "suggested buy" preview always sizes off the fixed starting
  // pool (matching the Scriptable widget); the compounding bankroll shown
  // in the strategy P&L card comes from computeStrategyPnl's replay instead.
  const allocation = useMemo(
    () =>
      decision && response
        ? calculateAllocation(
            decision.recommendation,
            decision.probability,
            response.quote.contractPrice,
            DEFAULT_CONFIG.simulatedStartingPool,
          )
        : null,
    [decision, response],
  );

  // V2/V3/V4 are fit server-side once a day against the live candle archive
  // (see netlify/functions/btc15-model-refit-scheduled.ts /
  // btc15-v4-refit-scheduled.ts) and fetched here rather than bundled into a
  // static source file — all three are `null` until enough live data has
  // accumulated for a first fit.
  const trainedV2Model = fittedModels.data?.v2 ?? null;
  const trainedV3Model = fittedModels.data?.v3 ?? null;
  const trainedV4Model = fittedModels.data?.v4 ?? null;

  const v2Prediction = useMemo(
    () => (response && trainedV2Model ? predictV2(response, trainedV2Model.model) : null),
    [response, trainedV2Model],
  );

  const v3Prediction = useMemo(
    () => (response && trainedV3Model ? predictV3(response, trainedV3Model.model) : null),
    [response, trainedV3Model],
  );

  // V4 additionally needs both V2 and V3 fitted models to compute its blend
  // inputs (no availability-mask fallback fitting — see
  // packages/signal-engine/src/v4/README.md), so it stays null until all
  // three exist, not just its own.
  const v4Prediction = useMemo(
    () =>
      response && trainedV4Model && trainedV2Model && trainedV3Model
        ? predictV4(
            response,
            trainedV4Model.model,
            trainedV2Model.model,
            trainedV3Model.model,
          )
        : null,
    [response, trainedV4Model, trainedV2Model, trainedV3Model],
  );

  // The reference baseline every other model is implicitly being compared
  // against — always available (no fitted model to wait on), so it's
  // memoized on `response` alone: one fresh flip per new round, not a new
  // flip on every re-render.
  const coinFlipPrediction = useMemo(
    () => (response ? predictCoinFlip() : null),
    [response],
  );

  // A second reference baseline, rolled once per round rather than once per
  // poll: `response` itself gets a new object identity on every poll tick
  // (even mid-round), which is fine for the coin flip's plain text above but
  // would make the dice's animation visibly re-tumble every poll instead of
  // once per round. Keying this memo on the round id instead — which stays
  // the same string across every poll of the same round — rolls once and
  // holds until the round actually changes.
  const roundId = response?.round.id ?? null;
  const dicePrediction = useMemo(
    () => (roundId !== null ? predictDice() : null),
    [roundId],
  );

  // Records V2/V3/V4's own independent paper-trading track record, same as
  // V1's applyResponse above — a no-op for a round where that version had
  // no fitted model yet (see recordModelObservation).
  useEffect(() => {
    if (response) applyV2Response(response, v2Prediction);
  }, [response, v2Prediction, applyV2Response]);
  useEffect(() => {
    if (response) applyV3Response(response, v3Prediction);
  }, [response, v3Prediction, applyV3Response]);
  useEffect(() => {
    if (response) applyV4Response(response, v4Prediction);
  }, [response, v4Prediction, applyV4Response]);
  useEffect(() => {
    if (response) applyCoinFlipResponse(response, coinFlipPrediction);
  }, [response, coinFlipPrediction, applyCoinFlipResponse]);
  useEffect(() => {
    if (response) applyDiceResponse(response, dicePrediction);
  }, [response, dicePrediction, applyDiceResponse]);

  // "V1 + take-profit" is server-authoritative only (see
  // useBtc15TakeProfitHistory): this just reads back whatever
  // btc15-tracker-scheduled.ts / btc15-takeprofit-scheduled.ts recorded.
  const takeProfitHistory = takeProfitHistoryQuery.data ?? emptyTakeProfitHistory();

  // Every strategy's paper record comes from the permanent bankroll ledger
  // (src/lib/bankrollLedger.ts). Until the server's ledger has data, the
  // same arithmetic runs over the stored histories instead, which reach
  // back only 1,200 rounds; the card says which one it is showing.
  const windowLedger = useMemo(
    () =>
      ledgerFromHistories({
        v1: history,
        takeProfit: takeProfitHistory,
        models: {
          v2: v2Hist,
          v3: v3Hist,
          v4: v4Hist,
          coinflip: coinFlipHist,
          dice: diceHist,
        },
      }),
    [history, takeProfitHistory, v2Hist, v3Hist, v4Hist, coinFlipHist, diceHist],
  );
  const serverLedger = bankrollQuery.data?.ledger ?? null;
  // Why the page is on the stored rounds, when it is: the ledger request
  // failed, hasn't answered yet, or answered that no ledger exists yet.
  const recordSource: TrackRecordSource =
    serverLedger &&
    LEDGER_STRATEGIES.some((key) => serverLedger.strategies[key].calls > 0)
      ? 'ledger'
      : bankrollQuery.isError
        ? 'window-error'
        : bankrollQuery.data
          ? 'window-pending'
          : 'window-loading';
  const summaries = useMemo(() => {
    const ledger =
      recordSource === 'ledger' && serverLedger ? serverLedger : windowLedger;
    return Object.fromEntries(
      LEDGER_STRATEGIES.map((key) => [key, summarizeStrategy(ledger.strategies[key])]),
    ) as Record<StrategyKey, StrategySummary>;
  }, [recordSource, serverLedger, windowLedger]);

  const trackRecordRows: TrackRecordRow[] = LEDGER_STRATEGIES.map((key) => ({
    key,
    name: STRATEGY_NAMES[key].name,
    descriptor: STRATEGY_NAMES[key].descriptor,
    note: STRATEGY_NOTES[key],
    summary: summaries[key],
    baseline: key === 'coinflip' || key === 'dice',
  }));

  const summaryText = useMemo(
    () =>
      response && decision && allocation
        ? buildCopySummary(
            response.signal,
            decision,
            allocation,
            accuracy,
            {
              pnl: summaries.v1.pnl,
              runCount: summaries.v1.runCount,
              busts: summaries.v1.busts,
              bankroll: summaries.v1.bankroll,
            },
            countdownSeconds,
          )
        : '',
    [response, decision, allocation, accuracy, summaries, countdownSeconds],
  );

  const errorMessage =
    query.error instanceof ApiClientError
      ? query.error.message
      : 'BTC 15m predictor data is temporarily unavailable.';
  const fromPaperTests = cameFromPaperTests(location.state);

  return (
    <div className={styles.page}>
      {!online && (
        <div className={styles.offlineBanner} role="status">
          You are offline. The last successful update stays on screen.
        </div>
      )}

      {fromPaperTests && <BackLink to="/paper-tests" label="Paper tests" />}

      <div className={styles.header}>
        <h1 className={styles.title}>15m Predictor</h1>
        <RefreshStatus
          // The server's own computation time: responses can be edge-cached
          // for a few seconds, so the browser's fetch time would overstate
          // freshness on a 15-minute round.
          updatedAt={response ? Date.parse(response.generatedAt) : undefined}
          isFetching={query.isFetching}
          isError={query.isError}
          onRefresh={() => query.refetch()}
        />
      </div>

      {/* One line, so the round's call starts near the top of a phone
          screen; the full notice opens in place, and the site-wide "not
          advice" banner stays pinned above this either way. */}
      <Disclaimer summary={READ_ONLY_SUMMARY} text={READ_ONLY_DISCLAIMER} />

      {query.isLoading && !response && (
        // Sized like the tabs and the round card they stand in for, so the
        // page doesn't jump when the first response lands.
        <>
          <Skeleton width="100%" height="3rem" />
          <Skeleton width="100%" height="24rem" />
        </>
      )}

      {query.isError && !response && (
        <div className={styles.errorBanner} role="alert">
          <span>{errorMessage}</span>
          <Button onClick={() => query.refetch()}>Try again</Button>
        </div>
      )}

      {response && decision && allocation && (
        <>
          {isPredictorDataStale(response) && (
            <div className={styles.offlineBanner} role="status">
              Price data is running behind (
              {Math.round(response.diagnostics.coinbaseDataAgeMs / 1000)}s old), so this
              round&apos;s numbers may be out of date.
            </div>
          )}
          <PredictorTabs tab={tab} onChange={setTab} />
          <TabPanel id="round" active={tab}>
            {/* The round at a glance: the call, what a simulated buy costs,
                and the live price against the target, in one card. */}
            <section className={styles.roundHero} aria-label="This round">
              <Btc15Decision
                showPrices={!live.data}
                skipReason={decision.reasonsToSkip[0]}
                leaning={decision.directionalAction}
                recommendation={decision.recommendation}
                strength={decision.strength}
                probability={decision.probability}
                secondsRemaining={countdownSeconds}
                roundOpenPrice={response.signal.roundOpenPrice}
                currentPrice={response.signal.price}
                distancePct={response.signal.distancePct}
                recentResults={recentFunded}
                simulatedStake={allocation.stake}
                noBuyReason={allocation.reason}
                record={{
                  rate: summaries.v1.hitRate,
                  total: summaries.v1.calls,
                  pnl: summaries.v1.pnl,
                  baselineRate: summaries.coinflip.hitRate,
                  baselineTotal: summaries.coinflip.calls,
                }}
              />
              <Btc15LiveRound
                live={live.data}
                path={live.path}
                position={livePosition}
                isError={live.isError}
              />
              {/* Once a simulated buy exists (and live prices are in), the
                  position row above carries its entry and stake; until then
                  this shows what a buy at the current price would be, or why
                  there isn't one. */}
              {decision.recommendation !== 'SKIP' && !(livePosition && live.data) && (
                <Btc15MarketSizing
                  quote={response.quote}
                  allocation={allocation}
                  // A $0 buy's reason is already shown under the call.
                  showReason={allocation.stake > 0}
                />
              )}
            </section>
            <Btc15PriceRulesNow
              secondsRemaining={countdownSeconds}
              yesAsk={response.quote.yesAsk}
              noAsk={response.quote.noAsk}
            />
            <Btc15ModelComparison
              v1Prediction={{
                action: decision.directionalAction,
                probability: decision.probability,
              }}
              v1Skipped={decision.recommendation === 'SKIP'}
              v2Prediction={v2Prediction}
              v2Meta={
                trainedV2Model
                  ? {
                      sampleCount: trainedV2Model.sampleCount,
                      walkForwardAccuracy: trainedV2Model.walkForwardAccuracy,
                    }
                  : null
              }
              v3Prediction={v3Prediction}
              v3Meta={
                trainedV3Model
                  ? {
                      sampleCount: trainedV3Model.sampleCount,
                      walkForwardAccuracy: trainedV3Model.walkForwardAccuracy,
                    }
                  : null
              }
              v4Prediction={v4Prediction}
              v4Meta={
                trainedV4Model
                  ? {
                      sampleCount: trainedV4Model.sampleCount,
                      walkForwardAccuracy: trainedV4Model.walkForwardAccuracy,
                    }
                  : null
              }
              coinFlipPrediction={coinFlipPrediction}
              dicePrediction={dicePrediction}
              diceFace={dicePrediction?.face ?? null}
            />
            <div className={styles.roundExtras}>
              <Btc15RoundLookup history={history} />
              <Btc15Details
                signal={response.signal}
                reasonsToSkip={decision.reasonsToSkip}
              />
              <Btc15Diagnostics response={response} />
            </div>
            <Btc15SignalOptIn />
          </TabPanel>
          <TabPanel id="record" active={tab}>
            <Btc15TrackRecord
              rows={trackRecordRows}
              rules={bankrollQuery.data?.rules ?? LEDGER_RULES}
              source={recordSource}
            />
            <Btc15HistoryTools history={history} summaryText={summaryText} />
          </TabPanel>
          <TabPanel id="rules" active={tab}>
            <ForwardTests
              priceRules={{
                data: priceRulesQuery.data,
                isError: priceRulesQuery.isError,
              }}
              riptide={{ data: riptideRuleQuery.data, isError: riptideRuleQuery.isError }}
              ethHourly={{
                data: ethHourlyRuleQuery.data,
                isError: ethHourlyRuleQuery.isError,
              }}
              pooled={{
                data: hourlyPooledRuleQuery.data,
                isError: hourlyPooledRuleQuery.isError,
              }}
            />
          </TabPanel>
          <TabPanel id="research" active={tab}>
            <p className={styles.panelNote}>
              What-if replays of Cut and Run&apos;s settled rounds at the Kalshi prices
              actually recorded, under other exit and sizing rules. Retrospective only;
              none of them is live.
            </p>
            <Btc15ShadowExitPolicyComparison
              eligibleRounds={shadowExitPolicyReportQuery.data?.eligibleRounds ?? 0}
              summaries={shadowExitPolicyReportQuery.data?.summaries ?? []}
              dollarPnl={shadowExitPolicyReportQuery.data?.dollarPnl ?? []}
            />
            <Btc15ShadowSizingPolicyComparison
              eligibleRounds={shadowExitPolicyReportQuery.data?.eligibleRounds ?? 0}
              sizingDollarPnl={shadowExitPolicyReportQuery.data?.sizingDollarPnl ?? []}
            />
            <Btc15MartingaleComparison
              eligibleRounds={shadowExitPolicyReportQuery.data?.eligibleRounds ?? 0}
              martingaleDollarPnl={
                shadowExitPolicyReportQuery.data?.martingaleDollarPnl ?? []
              }
            />
          </TabPanel>
        </>
      )}
    </div>
  );
}
